import moment from 'moment'
import * as React from 'react'

import { gradeText } from './gradeText'
import { providerConfigLookup, teacherImportsStudents } from './providerHelpers'
import RestoreClassModal from './restore_class_modal'

import { pluralize } from '../../../Shared/index'

interface MergedClassesSectionProps {
  classroom: any;
  onSuccess: (snackbarCopy: string) => void;
  allowRestore: boolean;
  user?: any;
  importMergedSecondaryStudents?: (secondaryClassroom: any) => void;
}

const MergedClassesSection = ({ classroom, onSuccess, allowRestore, user, importMergedSecondaryStudents, }: MergedClassesSectionProps) => {
  const [mergeToRestore, setMergeToRestore] = React.useState(null)

  const classroomMerges = classroom.classroom_merges || []

  if (!classroomMerges.length) { return null }

  const renderProviderAction = (merge) => {
    const { secondary_classroom_id, secondary_classroom_name, secondary_classroom_provider, secondary_classroom_updated_at, } = merge

    if (!secondary_classroom_provider) { return null }

    const lastImported = <span className="last-imported">Last imported {moment(secondary_classroom_updated_at).format('MMM D, YYYY')}</span>

    if (!teacherImportsStudents(secondary_classroom_provider)) { return lastImported }

    if (!importMergedSecondaryStudents) { return null }

    const isDisabled = providerConfigLookup[user?.provider]?.title !== secondary_classroom_provider

    return (
      <div className="merged-class-import">
        <button
          className={`quill-button primary outlined small focus-on-light ${isDisabled ? 'disabled' : ''}`}
          disabled={isDisabled}
          onClick={() => importMergedSecondaryStudents({ id: secondary_classroom_id, name: secondary_classroom_name })}
          type="button"
        >
          Import {secondary_classroom_provider} students
        </button>
        {lastImported}
      </div>
    )
  }

  const renderMergedClassRow = (merge) => {
    const { id, secondary_classroom_name, secondary_classroom_code, secondary_classroom_grade, student_count, created_at, } = merge

    return (
      <div className="merged-class-row" key={id}>
        <div className="merged-class-info">
          <h4>{secondary_classroom_name}</h4>
          <div className="merged-class-data">
            <span className="item">{student_count} {pluralize(student_count, 'student', 'students')}</span>
            <span className="bullet item">•</span>
            <span className="item">Class code: {secondary_classroom_code}</span>
            <span className="bullet item">•</span>
            <span className="item">{gradeText(secondary_classroom_grade)}</span>
            <span className="bullet item">•</span>
            <span className="item">Merged {moment(created_at).format('MMM D, YYYY')}</span>
          </div>
        </div>
        <div className="merged-class-actions">
          {renderProviderAction(merge)}
          {allowRestore && (
            <button
              className="quill-button outlined small red focus-on-light"
              onClick={() => setMergeToRestore(merge)}
              type="button"
            >
              Restore class
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className="merged-classes-section">
      <h3>Merged classes</h3>
      <p className="merged-classes-sub-header">These classes were merged into {classroom.name}. Their students now live here. You can restore a class to make it separate again.</p>
      {classroomMerges.map(renderMergedClassRow)}
      {mergeToRestore && (
        <RestoreClassModal
          classroomMerge={mergeToRestore}
          close={() => setMergeToRestore(null)}
          onSuccess={onSuccess}
          primaryClassroomName={classroom.name}
        />
      )}
    </div>
  )
}

export default MergedClassesSection
